/**
 * Accordion toggle behavior for the ACF "Accordion" block (`.accordion-item`).
 *
 * Iframe-aware. The SAME module runs in two places:
 *   1. Front end — imported into the main bundle (see assets/js/main.js).
 *   2. Block-editor canvas — enqueued via `enqueue_block_assets` (see
 *      functions.php). Since WP 7.0 the post editor renders block content in an
 *      <iframe>, so the script executes in the editor chrome and must reach
 *      into the canvas document to find the block markup, re-binding whenever
 *      the store changes (ACF blocks re-render their server-side preview).
 *
 * `initAccordions()` is idempotent — it flags bound items so repeated runs
 * (front-end reflows, editor store ticks) never double-bind.
 */
function initAccordions( root = document ) {
	root.querySelectorAll( '.accordion-item' ).forEach( ( item ) => {
		if ( item.dataset.accordionBound === 'true' ) {
			return;
		}

		const headline = item.querySelector( '.accordion-headline' );
		const button = item.querySelector( '.toggle-button' );
		const content = item.querySelector( '.accordion-content' );

		if ( ! headline || ! button || ! content ) {
			return;
		}

		item.dataset.accordionBound = 'true';

		headline.addEventListener( 'click', function () {
			const expanded = button.getAttribute( 'aria-expanded' ) === 'true';

			button.setAttribute( 'aria-expanded', String( ! expanded ) );

			if ( expanded ) {
				content.setAttribute( 'hidden', '' );
				content.classList.remove( 'max-h-500', 'opacity-100', 'pb-8' );
				content.classList.add( 'max-h-0', 'opacity-0', 'pb-0' );
			} else {
				content.removeAttribute( 'hidden' );
				content.classList.remove( 'max-h-0', 'opacity-0', 'pb-0' );
				content.classList.add( 'max-h-500', 'opacity-100', 'pb-8' );
			}

			button.classList.toggle( 'rotate-180', ! expanded );
		} );
	} );
}

/**
 * Shared bootstrapper for scripts that must run on the front end AND inside the
 * WP 7.0 block-editor canvas iframe.
 *
 * - Front end / standalone: runs `canvasInit(document)` once the DOM is ready.
 * - Editor chrome (window.wp present): resolves the canvas iframe document and
 *   runs `canvasInit(doc)` now and on every (debounced) store change.
 */
function dequeBootstrapCanvasScript( canvasInit ) {
	const runWhenReady = ( fn ) => {
		if ( document.readyState === 'loading' ) {
			document.addEventListener( 'DOMContentLoaded', fn );
		} else {
			fn();
		}
	};

	const inEditor =
		typeof window !== 'undefined' &&
		window.wp &&
		window.wp.data &&
		window.wp.domReady;

	if ( ! inEditor ) {
		runWhenReady( () => canvasInit( document ) );
		return;
	}

	window.wp.domReady( () => {
		const canvasDoc = () => {
			const iframe = document.querySelector(
				'iframe[name="editor-canvas"]'
			);
			return iframe && iframe.contentDocument
				? iframe.contentDocument
				: document;
		};

		let scheduled;
		const reinit = () => canvasInit( canvasDoc() );

		reinit();
		window.wp.data.subscribe( () => {
			clearTimeout( scheduled );
			scheduled = setTimeout( reinit, 300 );
		} );
	} );
}

// Expose for reuse/debugging in either context.
if ( typeof window !== 'undefined' ) {
	window.dequeInitAccordions = initAccordions;
	window.dequeBootstrapCanvasScript = dequeBootstrapCanvasScript;
}

dequeBootstrapCanvasScript( initAccordions );
